import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  ShieldCheck,
  Users,
  AlertTriangle,
  MapPin,
  Scale,
  Sparkles,
  Gauge,
  TrendingDown,
  Activity,
  Loader2,
  XCircle,
  Home,
  Mail,
  Bell,
  HelpCircle,
  BookOpen,
  Upload,
  LayoutDashboard,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { StatusChip, type SubmissionStatus } from "@/components/status-chip";
import { AuthGate } from "@/components/auth-gate";
import { DocumentIntegrityBanner } from "@/components/review/document-integrity-banner";
import { Button } from "@/components/ui/button";
import { useCases, useCase, type Case, type SubmissionMethod } from "@/lib/cases";
import {
  useFullWorkspaceBundle,
  reviewToCase,
  type FullWorkspaceBundle,
} from "@/hooks/use-real-reviews";
import { useReviewJobs, JOB_LABEL, type ReviewJobRow } from "@/hooks/use-review-jobs";
import {
  friendlyTimelineTitle,
  friendlyTimelineDescription,
  suppressSupersededTimelineEvents,
} from "@/lib/review/timeline-presentation";
import { resolveCaseOverviewHydration } from "@/lib/review/status-hydration";


import type {
  ReviewFile,
  TimelineEvent,
  AIFindings,
  InvoiceData,
  LegalResearch,
  MarketComparison,
  Report as StoredReport,

} from "@/lib/db/models";
import { largestVariances } from "@/lib/report/invoice-market-comparison";
import {
  buildCanonicalInvoiceMarketComparison,
  canonicalChargesFor,
  selectQuestionableCharges,
} from "@/lib/review/case-overview-model";
import { buildReportSummary } from "@/lib/report/report-summary";
import { customerReviewPreferences, NOT_PROVIDED } from "@/lib/review/customer-review-preferences";

import { buildLegalSummary, type LegalSupportLevel } from "@/lib/report/legal-summary";
import {
  ANALYSIS_CONFIDENCE_LABEL,
  NOT_CALCULATED,
  confidenceBand,
  confidenceDescriptor,
  formatConfidencePct,
} from "@/lib/confidence/confidence-vocabulary";
import { formatClaimStamp, processingElapsed } from "@/lib/claim-timestamps";

import {
  buildReport,
  buildReportFromBundle,
  extractInvoiceReportFields,
  currency,
  MISSING_FIELD,
  NOT_EXTRACTED,
  type InvoiceReportFields,
} from "@/lib/report";
import type { JobType } from "@/lib/ai/types";


type Search = { id?: string };

export const Route = createFileRoute("/review")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    id: typeof s.id === "string" ? s.id : undefined,
  }),
  head: () => ({
    meta: [
      { title: "ClaimGuard | Review Started" },
      {
        name: "description",
        content:
          "Your invoice has been securely received and our AI has already started reviewing it.",
      },
    ],
  }),
  component: () => (
    <AuthGate>
      <ReviewPage />
    </AuthGate>
  ),
});

const METHOD_LABEL: Record<SubmissionMethod, string> = {
  photo: "Photo",
  upload: "File upload",
  email: "Email forwarding",
};

const STEP_INFO = [
  {
    label: "Upload Complete",
    desc: "Invoice securely uploaded and added to your review queue.",
  },
  {
    label: "OCR Complete",
    desc: "Vendor, invoice number, dates, individual charges, and totals extracted.",
  },
  {
    label: "Invoice Normalized",
    desc: "Charges organized into storage, administrative, release, environmental, and lien categories.",
  },
  {
    label: "Legal Research",
    desc: "State statutes, consumer protections, storage regulations, and notice requirements reviewed.",
  },
  {
    label: "Market Comparison",
    desc: "Benchmarked against comparable facilities within your ZIP code.",
  },
  {
    label: "AI Findings",
    desc: "Pricing differences, documentation gaps, duplicate charges, and excessive fees identified.",
  },
  {
    label: "Building Claim Intelligence Package™",
    desc: "Assembling the executive report with findings, evidence, citations, and recommendations.",
  },
  {
    label: "Report Ready",
    desc: "Your Claim Intelligence Package™ is ready to open.",
  },
] as const;
const STEPS = STEP_INFO.map((s) => s.label) as readonly string[];

// Rolling narrative that surfaces beneath the pipeline as each stage advances.
const STAGE_UPDATES: { done: string[]; active: string }[] = [
  { done: [], active: "Invoice received — preparing analysis…" },
  {
    done: ["Invoice received", "Document quality verified"],
    active: "Extracting vendor, totals, and line items via OCR…",
  },
  {
    done: [
      "Vendor identified",
      "Invoice total detected",
      "Invoice date detected",
      "Line items extracted",
    ],
    active: "Normalizing charges into standard categories…",
  },
  {
    done: [
      "Charges organized by category",
      "Storage duration calculated",
    ],
    active: "Researching applicable state laws and consumer protections…",
  },
  {
    done: [
      "Applicable regulations located",
      "State jurisdiction confirmed",
    ],
    active: "Comparing your invoice against local ZIP code pricing…",
  },
  {
    done: [
      "Local market pricing benchmarked",
      "Comparable facilities identified",
    ],
    active: "Ranking negotiation opportunities and evidence strength…",
  },
  {
    done: [
      "Negotiation opportunities identified",
      "Evidence and citations assembled",
    ],
    active: "Building your professional review package…",
  },
  {
    done: [
      "Professional review package assembled",
      "Findings, evidence, and citations verified",
    ],
    active: "Your Claim Intelligence Package™ is ready.",
  },
];

/**
 * Neutral Case Overview placeholder shown while the authoritative claim
 * status is still hydrating. Contains no business status wording.
 */
function CaseOverviewSkeleton() {
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <div className="container-page py-10" data-testid="case-overview-skeleton">
        <div className="h-4 w-32 animate-pulse rounded bg-secondary" />
        <div className="mt-8 h-10 w-2/3 max-w-xl animate-pulse rounded bg-secondary" />
        <div className="mt-4 h-5 w-1/2 max-w-md animate-pulse rounded bg-secondary" />
        <div className="mt-8 h-40 w-full animate-pulse rounded-2xl bg-secondary" />
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="h-28 animate-pulse rounded-2xl bg-secondary" />
          <div className="h-28 animate-pulse rounded-2xl bg-secondary" />
        </div>
        <span className="sr-only">Loading your claim…</span>
      </div>
      <SiteFooter />
    </div>
  );
}


function ReviewPage() {
  const { id } = Route.useSearch();
  const cases = useCases();
  const demoParamCase = useCase(id ?? "");
  const bundle = useFullWorkspaceBundle(id);
  const {
    review: realReview,
    files: bundleFiles,
    timeline,
    invoiceData,
    findings,
    report,
    loading: realLoading,
    error: realError,
    refresh: refreshBundle,
  } = bundle;
  const realCase = useMemo(
    () =>
      realReview
        ? reviewToCase(
            realReview,
            bundleFiles.map((f) => ({
              name: f.file_name,
              size: f.file_size ?? 0,
              type: f.mime_type ?? "",
            })),
          )
        : null,
    [realReview, bundleFiles],
  );
  const files: ReviewFile[] = bundleFiles;
  const c = realCase ?? demoParamCase ?? cases[0];
  const isReal = Boolean(realCase);
  const { jobs } = useReviewJobs(realReview?.id);

  // Refresh the persisted bundle (invoice_data / ai_findings / reports) each
  // time the job statuses change, so vendor/total/savings/report tracks the
  // pipeline live without a manual reload.
  const jobsSignature = useMemo(
    () => jobs.map((j) => `${j.job_type}:${j.status}`).join("|"),
    [jobs],
  );
  useEffect(() => {
    if (!realReview?.id) return;
    if (!jobsSignature) return;
    refreshBundle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsSignature, realReview?.id]);
  // While a report doesn't yet exist, poll the bundle so newly persisted
  // ai_findings/invoice_data rows appear even if job rows don't change.
  useEffect(() => {
    if (!realReview?.id || report) return;
    const t = window.setInterval(() => refreshBundle(), 8000);
    return () => window.clearInterval(t);
  }, [realReview?.id, report, refreshBundle]);

  const jobsRef = useRef<ReviewJobRow[]>([]);
  jobsRef.current = jobs;

  // Keep the server-side pipeline advancing while this page is open.
  // A single one-shot tick was not enough: a stage waiting on retry backoff
  // (or a lease released by the watchdog) needs another nudge, otherwise the
  // review sits at "Retrying" forever. Ticks are idempotent server-side —
  // only claimable jobs are ever executed.
  const tickInFlight = useRef(false);
  useEffect(() => {
    const rid = realReview?.id;
    if (!rid) return;
    async function tickOnce() {
      if (tickInFlight.current) return;
      const pending = jobsRef.current.filter(
        (j) => j.status === "queued" || j.status === "retrying" || j.status === "processing",
      );
      if (!jobsRef.current.length || !pending.length) return;
      tickInFlight.current = true;
      try {
        const { tickAIPipeline } = await import("@/lib/ai.functions");
        const res = await tickAIPipeline({ data: { review_id: rid } });
        console.log("[review] pipeline tick outcomes:", res.outcomes);
      } catch (e) {
        console.error("[review] pipeline tick failed:", e);
      } finally {
        tickInFlight.current = false;
      }
    }
    void tickOnce();
    const t = window.setInterval(() => void tickOnce(), 30000);
    return () => window.clearInterval(t);
  }, [realReview?.id]);

  // One canonical financial object for the whole page.
  const canonicalReport = useMemo(
    () => buildReviewReport(c, bundle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [c, bundle.review, bundle.invoiceData, bundle.findings, bundle.findingItems, bundle.marketComparisons, bundle.marketResearch, bundle.report],
  );

  // The immutable Report Summary — the only money source for this page.
  const reportSummary = useMemo(
    () =>
      buildReportSummary({
        review: bundle.review ?? null,
        invoiceData: bundle.invoiceData ?? null,
        findings: bundle.findings ?? null,
        findingItems: bundle.findingItems ?? [],
        legalResearch: bundle.legalResearch ?? [],
        marketComparisons: bundle.marketComparisons ?? [],
        marketResearch: bundle.marketResearch ?? [],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundle.review, bundle.invoiceData, bundle.findings, bundle.findingItems, bundle.legalResearch, bundle.marketComparisons, bundle.marketResearch],
  );


  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((v) => v + 1), 1600);
    return () => window.clearInterval(t);
  }, []);

  // Canonical progress. Derived ONLY from persisted job completions — a
  // queued or retrying stage never advances the step, so the header, pulse,
  // narrative, and dashboard all agree with the backend.
  const completedJobTypes = useMemo(
    () =>
      new Set<JobType>(
        jobs.filter((j) => j.status === "completed").map((j) => j.job_type as JobType),
      ),
    [jobs],
  );
  const jobsDerived = useMemo(() => {
    if (!jobs.length && !report) return null;
    const done = completedJobTypes;
    const failed = jobs.some((j) => j.status === "failed");
    if (report) return { activeStep: 8, complete: true, failed };
    let step = 1; // Upload Complete
    if (done.has("ocr")) step = 2;
    if (done.has("invoice_normalization")) step = 3;
    if (done.has("legal_research")) step = 4;
    if (done.has("market_comparison")) step = 5;
    if (done.has("ai_findings")) step = 6;
    if (done.has("negotiation_strategy")) step = 7;
    if (done.has("report_generation") || done.has("notification")) step = 8;
    const complete = step >= 8;
    return { activeStep: step, complete, failed };
  }, [jobs, report, completedJobTypes]);

  const derived = useMemo(() => {
    if (c) {
      const map: Record<string, { step: number; chip: SubmissionStatus }> = {
        received: { step: 1, chip: "ai_review_pending" },
        reviewing: { step: 4, chip: "ai_review_in_progress" },
        report_ready: { step: STEPS.length, chip: "findings_ready" },
        self_serve: { step: STEPS.length, chip: "findings_ready" },
        negotiating: { step: STEPS.length, chip: "expert_negotiation_requested" },
        waiting_vendor: { step: STEPS.length, chip: "expert_negotiation_requested" },
        offer_received: { step: STEPS.length, chip: "expert_negotiation_requested" },
        completed: { step: STEPS.length, chip: "completed" },
      };
      return map[c.status] ?? map.received;
    }
    const step = Math.min(STEPS.length, 1 + (tick % (STEPS.length + 1)));
    return {
      step,
      chip: (step >= STEPS.length ? "findings_ready" : "ai_review_in_progress") as SubmissionStatus,
    };
  }, [c, tick]);

  const effective = jobsDerived
    ? {
        step: jobsDerived.activeStep,
        chip: (jobsDerived.complete
          ? "findings_ready"
          : "ai_review_in_progress") as SubmissionStatus,
      }
    : derived;
  const complete = effective.step >= STEPS.length || Boolean(report);
  const jobsFailed = jobsDerived?.failed ?? false;

  const fileNumber = c?.id ?? "FILE-2026-000042";
  const method = (c?.method ?? "upload") as SubmissionMethod;
  const submittedAt = c?.createdAt ? new Date(c.createdAt) : null;
  const navigate = useNavigate();

  const [showProgress, setShowProgress] = useState(true);
  // When we detect completion, keep details visible; when just navigated to a
  // fresh in-progress review, remain open by default.
  useEffect(() => {
    if (complete) setShowProgress(true);
  }, [complete]);

  // ── Status hydration guard ────────────────────────────────────────────
  // The authoritative claim status comes from the persisted review row for
  // THIS claim id. Until it is known we render a neutral skeleton — never a
  // business status such as "Review Started"/processing (which would flash
  // for report-ready claims and could reuse a previous claim's row).
  const hydration = resolveCaseOverviewHydration({
    requestedId: id,
    loading: realLoading,
    review: realReview,
  });
  if (!hydration.resolved) {
    return <CaseOverviewSkeleton />;
  }


  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <div className="container-page py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Link>

        {realReview?.id && (
          <DocumentIntegrityBanner reviewId={realReview.id} onResumed={refreshBundle} />
        )}


        {/* Hero */}
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-trust animate-fade-in">
            {complete ? "Report ready" : "Review status"}
          </div>
          <h1 className="mt-2 text-4xl text-navy md:text-5xl animate-fade-in">
            <span className="inline-flex items-center gap-3">
              <span
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full md:h-12 md:w-12 animate-scale-in ${
                  complete
                    ? "bg-savings text-savings-foreground"
                    : "bg-trust text-trust-foreground"
                }`}
              >
                {complete ? (
                  <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6" />
                ) : (
                  <Loader2 className="h-5 w-5 animate-spin md:h-6 md:w-6" />
                )}
              </span>
              {complete ? "Your Report Is Ready" : "Review Started"}
            </span>
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground animate-fade-in">
            {complete ? "We finished the analysis." : "We've got it from here."}
          </p>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground animate-fade-in">
            {complete
              ? "Your Claim Intelligence Package™ is ready — open it below to review findings, evidence, and next steps."
              : "Your invoice has been securely received and our AI has already started reviewing it. You don't need to stay on this page — we'll notify you as soon as your professional findings report is ready."}
          </p>
        </div>

        {/* Always-visible compact progress pulse */}
        {!complete && (
          <PipelinePulse activeStep={effective.step} jobs={jobs} />
        )}

        {!complete && (
        <div className="mt-8 animate-fade-in">
          <div className="rounded-2xl border border-savings/20 bg-savings-soft p-8 shadow-card">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-savings text-savings-foreground">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-display text-2xl text-navy">
                  You&apos;re free to leave this page
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Your review continues automatically even if you close this browser or turn off your computer.
                  You&apos;ll be notified when your report is ready through:
                </p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-3">
                  {[
                    { icon: Mail, label: "Email" },
                    { icon: LayoutDashboard, label: "Dashboard" },
                    { icon: Bell, label: "In-app notification" },
                  ].map((item) => (
                    <li
                      key={item.label}
                      className="flex items-center gap-2 rounded-lg bg-white/60 px-3 py-2 text-sm font-medium text-navy"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-savings" />
                      <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        )}


        {/* Primary + secondary CTAs */}
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center animate-fade-in">
          {report ? (
            <Button
              size="lg"
              className="h-12 px-8 text-base font-semibold"
              onClick={() => navigate({ to: "/report/$id", params: { id: fileNumber } })}
            >
              <FileText className="mr-2 h-5 w-5" />
              Open Claim Intelligence Package™
            </Button>
          ) : (
            <Button
              size="lg"
              className="h-12 px-8 text-base font-semibold"
              onClick={() => navigate({ to: "/dashboard", search: {} })}
            >
              <LayoutDashboard className="mr-2 h-5 w-5" />
              Go to My Dashboard
            </Button>
          )}
          {!complete && (
            <Button
              variant="outline"
              size="lg"
              className="h-12 px-6 text-base"
              onClick={() => setShowProgress((v) => !v)}
            >
              <Activity className="mr-2 h-4 w-4" />
              {showProgress ? "Hide" : "Watch"} Live Review Progress
            </Button>
          )}

        </div>


        {/* Auto-return countdown */}
        {!complete && <AutoReturnCountdown />}

        {/* File summary */}
        <div className="mt-8 grid gap-4 rounded-2xl border border-border bg-card p-6 shadow-card sm:grid-cols-3">
          <SummaryRow
            label="Review ID"
            value={<span className="font-mono text-navy">{fileNumber}</span>}
          />
          <SummaryRow label="Submission Method" value={METHOD_LABEL[method]} />
          <SummaryRow
            label="Status"
            value={
              <span className="text-navy">{complete ? "Report Ready" : "In Review"}</span>
            }
          />
          <SummaryRow
            label={submittedAt ? "Submitted" : "Estimated Review Time"}
            value={
              submittedAt ? (
                <span className="inline-flex items-center gap-1.5 text-navy">
                  <Clock className="h-4 w-4 text-trust" />
                  {formatClaimStamp(c?.timestamps?.submittedAt ?? c?.createdAt ?? null)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-navy">
                  <Clock className="h-4 w-4 text-trust" /> ~10–60 minutes
                </span>
              )
            }
          />
          {c?.timestamps?.reportCompletedAt && (
            <SummaryRow
              label="Completed"
              value={
                <span className="inline-flex items-center gap-1.5 text-navy">
                  <Clock className="h-4 w-4 text-savings" />
                  {formatClaimStamp(c.timestamps.reportCompletedAt)}
                </span>
              }
            />
          )}
          {c?.timestamps && processingElapsed(c.timestamps) && (
            <SummaryRow
              label="Processing Time"
              value={<span className="text-navy">{processingElapsed(c.timestamps)}</span>}
            />
          )}
        </div>




        {isReal && (invoiceData || findings) && (
          <LiveInvoiceSnapshot
            review={realReview}
            invoiceData={invoiceData}
            findings={findings}
            canonical={canonicalReport}
            financials={reportSummary.financials}

            report={report}
            legalAuthorities={
              buildLegalSummary(bundle?.legalResearch ?? [], canonicalReport.state).total
            }
            currentStageLabel={STEP_INFO[Math.max(0, effective.step - 1)]?.label ?? STEP_INFO[0].label}
          />
        )}

        {id && realLoading && !realCase && (
          <div className="mt-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Loading your review…
          </div>
        )}
        {id && !realLoading && !realCase && realError && (
          <div className="mt-4 rounded-xl border border-warn/30 bg-warn/5 p-4 text-sm text-warn">
            We couldn&apos;t load this review right now. Showing a sample view.
          </div>
        )}

        {isReal && (files.length > 0 || timeline.length > 0) && (
          <RealReviewDetails
            files={files}
            timeline={timeline}
            reviewRow={bundle?.review ?? null}
            legalResearchPersisted={(bundle?.legalResearch ?? []).length > 0}
          />
        )}


        {/* What happens next — educational */}
        {!complete && (
          <div className="mt-8">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-trust">
              <Sparkles className="h-4 w-4" /> What happens next?
            </div>
            <h2 className="mt-2 font-display text-2xl text-navy">
              Background review in progress
            </h2>
            <ol className="mt-6 space-y-3">
              {[
                "Reading your invoice",
                "Researching pricing",
                "Reviewing applicable laws",
                "Building your findings",
                "Creating your professional report",
                "Notifying you when it\u2019s ready",
              ].map((label, i) => (
                <li
                  key={label}
                  className="flex items-center gap-3 rounded-xl border border-border bg-white p-3 text-sm text-navy"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-savings-soft text-xs font-bold text-savings">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </span>
                  {label}
                </li>
              ))}
            </ol>
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-secondary/60 px-4 py-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Estimated completion: <span className="font-semibold text-navy">10–60 minutes</span>
            </div>
          </div>
        )}

        {/* Live progress sections (toggleable / always visible when complete) */}
        {(showProgress || complete) && (
          <>
            {!complete && <LatestAIUpdate activeStep={effective.step} />}
            {!complete && c && (
              <AIDiscoveries
                c={c}
                activeStep={effective.step}
                invoice={isReal ? extractInvoiceReportFields(invoiceData, realReview) : null}
              />
            )}
            {jobs.length > 0 && !complete && (
              <JobStatusList jobs={jobs} failed={jobsFailed} activeStep={effective.step} />
            )}
            {complete ? (
              <FindingsPreview c={c} bundle={bundle} />
            ) : (
              <ProgressChecklist activeStep={effective.step} />
            )}
          </>
        )}

        {/* Action cards */}
        {!complete && (
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            <ActionCard
              icon={LayoutDashboard}
              title="Dashboard"
              description="Continue using ClaimGuard while your review finishes."
              buttonLabel="Go to Dashboard"
              buttonVariant="default"
              onClick={() => navigate({ to: "/dashboard", search: {} })}
            />
            <ActionCard
              icon={Upload}
              title="Submit Another Invoice"
              description="Need another invoice reviewed?"
              buttonLabel="Upload Another"
              buttonVariant="outline"
              onClick={() => navigate({ to: "/submit" })}
            />
            <ActionCard
              icon={HelpCircle}
              title="Need Help?"
              description="Questions? Contact Support or browse our Knowledge Base and FAQ."
              buttonLabel="Contact Support"
              buttonVariant="outline"
              onClick={() => navigate({ to: "/dashboard", search: {} })}
            />
          </div>
        )}

      </div>
      <SiteFooter />
    </div>
  );
}

/* ---------------- Auto-return countdown ---------------- */

function AutoReturnCountdown() {
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(8);
  const [visible, setVisible] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const showTimer = window.setTimeout(() => setVisible(true), 3000);
    return () => window.clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (!visible || seconds <= 0) return;
    const t = window.setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          window.clearInterval(t);
          if (!cancelledRef.current) {
            navigate({ to: "/dashboard", search: {} });
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [visible, seconds, navigate]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="mt-6 flex items-center gap-4 rounded-xl border border-border bg-card p-4 text-sm animate-fade-in">
      <Home className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">
        Returning you to your dashboard in{" "}
        <span className="font-semibold text-navy">{seconds}</span>…
      </span>
      <div className="ml-auto flex items-center gap-2">
        <Button size="sm" onClick={() => navigate({ to: "/dashboard", search: {} })}>
          Go Now
        </Button>
        <Button size="sm" variant="ghost" onClick={cancel}>
          Stay Here
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Action cards ---------------- */

function ActionCard({
  icon: Icon,
  title,
  description,
  buttonLabel,
  buttonVariant,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  buttonLabel: string;
  buttonVariant: "default" | "outline";
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card card-lift">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
        <Icon className="h-5 w-5 text-navy" />
      </div>
      <h3 className="mt-4 font-display text-lg text-navy">{title}</h3>
      <p className="mt-1 flex-1 text-sm text-muted-foreground">{description}</p>
      <Button variant={buttonVariant} className="mt-4 w-full" onClick={onClick}>
        {buttonLabel}
      </Button>
    </div>
  );
}

/* ---------------- Progress checklist ---------------- */

function ProgressChecklist({ activeStep }: { activeStep: number }) {
  const pct = Math.min(100, Math.round((activeStep / STEPS.length) * 100));
  const currentLabel = STEP_INFO[Math.max(0, activeStep - 1)]?.label ?? STEP_INFO[0].label;
  const remainingSteps = Math.max(0, STEPS.length - activeStep);
  const etaMinutes = Math.max(2, remainingSteps * 4);
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card lg:col-span-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
          <Sparkles className="h-4 w-4" /> What we&apos;re doing right now
        </div>
        <h2 className="mt-2 font-display text-2xl text-navy">Working on your invoice</h2>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4 rounded-xl border border-border bg-secondary/40 p-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Stage {Math.min(activeStep, STEPS.length)} of {STEPS.length}
            </div>
            <div className="mt-1 font-display text-lg text-navy">{currentLabel}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Estimated completion
            </div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-trust">
              <Clock className="h-4 w-4" />
              {remainingSteps === 0
                ? "Wrapping up"
                : `Approximately ${etaMinutes} min remaining`}
            </div>
          </div>
        </div>

        <ol className="mt-6 space-y-3">
          {STEP_INFO.map(({ label, desc }, i) => {
            const idx = i + 1;
            const done = idx < activeStep;
            const current = idx === activeStep;
            return (
              <li
                key={label}
                className={`flex items-start gap-3 rounded-xl border p-3 transition-all duration-300 ${
                  current
                    ? "border-trust bg-trust-soft shadow-[0_0_0_1px_hsl(var(--trust)/0.15),0_8px_24px_-12px_hsl(var(--trust)/0.35)] ring-1 ring-trust/25"
                    : done
                      ? "border-border bg-white"
                      : "border-dashed border-border bg-white/60"
                }`}
              >
                <div className="mt-0.5">
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-savings" />
                  ) : current ? (
                    <Loader2 className="h-5 w-5 animate-spin text-trust" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/40" />
                  )}
                </div>
                <div className="flex-1">
                  <div
                    className={`text-sm font-semibold ${
                      done || current ? "text-navy" : "text-muted-foreground"
                    }`}
                  >
                    {label}
                  </div>
                  <div
                    className={`mt-1 text-xs leading-relaxed ${
                      current ? "text-trust/90" : "text-muted-foreground"
                    }`}
                  >
                    {desc}
                  </div>
                  {current && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-trust">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-trust/60" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-trust" />
                      </span>
                      In progress
                    </div>
                  )}
                </div>
                <div className="text-xs font-semibold text-muted-foreground">
                  {String(idx).padStart(2, "0")}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Overall progress</span>
            <span>{pct}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-gradient-to-r from-trust to-savings transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      <aside className="rounded-2xl border border-border bg-navy p-6 text-navy-foreground shadow-card">
        <div className="text-xs font-semibold uppercase tracking-wider text-white/70">
          What you&apos;ll receive
        </div>
        <h3 className="mt-2 font-display text-xl">Your Findings Package</h3>
        <ul className="mt-4 space-y-3 text-sm text-white/85">
          {[
            "Plain-English breakdown of every charge",
            "Local ZIP code price comparison",
            "State laws and regulations that apply",
            "Negotiation leverage and talking points",
            "A ready-to-send professional letter",
          ].map((x) => (
            <li key={x} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-savings" />
              {x}
            </li>
          ))}
        </ul>
        <div className="mt-6 rounded-xl bg-white/5 p-4 text-xs leading-relaxed text-white/75">
          Every recommendation in your report is supported by documented pricing comparisons,
          applicable regulations, or invoice evidence whenever available.
        </div>
        <div className="mt-3 rounded-xl bg-white/5 p-4 text-xs text-white/70">
          You&apos;ll get an email and in-app notification when it&apos;s ready. No action needed right now.
        </div>
      </aside>
    </div>
  );
}

/* ---------------- Latest AI Update card ---------------- */

function LatestAIUpdate({ activeStep }: { activeStep: number }) {
  const idx = Math.min(STAGE_UPDATES.length - 1, Math.max(0, activeStep - 1));
  const update = STAGE_UPDATES[idx];
  return (
    <div className="mt-6 rounded-2xl border border-trust/25 bg-gradient-to-br from-trust-soft via-card to-card p-6 shadow-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-trust">
          <Activity className="h-4 w-4" /> Latest AI update
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-trust">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-trust/60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-trust" />
          </span>
          Live
        </span>
      </div>
      <ul className="mt-4 space-y-2 text-sm">
        {update.done.map((line) => (
          <li key={line} className="flex items-start gap-2 text-navy">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-savings" />
            <span>{line}</span>
          </li>
        ))}
        <li className="flex items-start gap-2 font-medium text-trust">
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
          <span>{update.active}</span>
        </li>
      </ul>
    </div>
  );
}

/* ---------------- AI Discoveries ---------------- */

/** "towing_storage" → "Tow & Storage Invoice"; never show an internal value. */
function prettyInvoiceType(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const key = value.trim().toLowerCase();
  if (!key || key === MISSING_FIELD.toLowerCase() || key === NOT_EXTRACTED.toLowerCase()) {
    return undefined;
  }
  const MAP: Record<string, string> = {
    towing_storage: "Tow & Storage Invoice",
    towing: "Towing Invoice",
    storage: "Storage Invoice",
    auto_repair: "Auto Repair Invoice",
    body_shop: "Body Shop Invoice",
    medical: "Medical Invoice",
    freight: "Freight Invoice",
    general: "General Invoice",
  };
  if (MAP[key]) return MAP[key];
  const cleaned = value.replace(/[_-]+/g, " ").trim();
  if (!cleaned) return undefined;
  if (/[a-z]/.test(cleaned) && /[A-Z]/.test(cleaned)) return cleaned;
  return cleaned
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** A canonical field value, or undefined when the pipeline hasn't produced it. */
function knownValue(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (!v || v === MISSING_FIELD || v === NOT_EXTRACTED) return undefined;
  return v;
}

function AIDiscoveries({
  c,
  activeStep,
  invoice,
}: {
  c: Case;
  activeStep: number;
  invoice?: InvoiceReportFields | null;
}) {
  if (activeStep < 2) return null;
  const d = c.details ?? {};
  // Canonical extraction wins over the review row so this card can never
  // disagree with the Invoice Summary or the professional report.
  const vendor = knownValue(invoice?.vendorName) ?? knownValue(d.vendor);
  const invoiceType = prettyInvoiceType(invoice?.invoiceType) ?? prettyInvoiceType(d.invoiceType);
  const invoiceTotal =
    invoice?.totalDue != null ? currency(invoice.totalDue) : knownValue(d.totalAmount);
  const invoiceDate = knownValue(invoice?.invoiceDate) ?? knownValue(d.invoiceDate);
  const zip = knownValue(invoice?.zip) ?? knownValue(d.zip);
  const items: { label: string; value?: string; known: boolean }[] = [
    { label: "Vendor identified", value: vendor, known: Boolean(vendor) },
    { label: "Invoice type detected", value: invoiceType, known: Boolean(invoiceType) },
    { label: "Invoice total identified", value: invoiceTotal, known: Boolean(invoiceTotal) },
    {
      label: "Invoice date detected",
      value: invoiceDate ?? "Not Detected",
      known: Boolean(invoiceDate),
    },
    { label: "ZIP code identified", value: zip, known: Boolean(zip) },
    {
      label: "Documents uploaded",
      value: c.files?.length ? `${c.files.length} file${c.files.length === 1 ? "" : "s"}` : undefined,
      known: Boolean(c.files?.length),
    },
  ];
  const knownCount = items.filter((i) => i.known).length;
  if (knownCount === 0) return null;

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-trust">
          <Sparkles className="h-4 w-4" /> What our AI has discovered
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {knownCount} detail{knownCount === 1 ? "" : "s"} extracted
        </span>
      </div>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {items.map((it) => (
          <li
            key={it.label}
            className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
              it.known
                ? "border-border bg-white"
                : "border-dashed border-border bg-white/50 text-muted-foreground"
            }`}
          >
            {it.known ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-savings" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" />
            )}
            <div className="flex-1">
              <div className={`font-medium ${it.known ? "text-navy" : ""}`}>{it.label}</div>
              {it.value && (
                <div className="mt-0.5 text-xs text-muted-foreground truncate">{it.value}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Live invoice snapshot (persisted pipeline data) ---------------- */

function LiveInvoiceSnapshot({
  review,
  invoiceData,
  findings,
  canonical,
  financials,
  report,
  legalAuthorities,
  currentStageLabel,
}: {
  review: import("@/lib/db/models").Review | null;
  invoiceData: InvoiceData | null;
  findings: AIFindings | null;
  /** The one financial object this page renders from. */
  canonical: ReturnType<typeof buildReport>;
  /** Immutable Report Summary financials — the only money source here. */
  financials: ReturnType<typeof buildReportSummary>["financials"];
  report: StoredReport | null;
  legalAuthorities: number;
  currentStageLabel: string;
}) {
  const invoice = extractInvoiceReportFields(invoiceData, review);
  const vendor = invoice.vendorName;
  const total = invoice.totalDue ?? canonical.invoiceTotal ?? review?.total_amount ?? null;
  /* FINANCIAL DETERMINISM: every dollar on this card is read straight from the
     immutable Report Summary the Professional Report publishes. No range, no
     midpoint, no local subtraction. */
  const fairPayment = financials.supportedAmount > 0 ? financials.supportedAmount : null;
  const settlementGoal = financials.settlementGoal > 0 ? financials.settlementGoal : null;
  const potential = canonical.estimatedSavings;


  const confidence =
    typeof canonical.confidence === "number" && canonical.confidence > 0
      ? canonical.confidence
      : typeof findings?.confidence === "number"
        ? Math.round(findings.confidence * 100)
        : null;

  // A value the pipeline has not produced yet is honest about that state
  // instead of printing a placeholder dash.
  const pending = report ? "Not available" : "Calculating…";

  const items: {
    label: string;
    value: string;
    tone: "navy" | "trust" | "savings";
    sub?: string;
  }[] = [
    {
      label: "Vendor",
      value: vendor && vendor !== MISSING_FIELD ? vendor : pending,
      tone: "navy",
    },
    { label: "Invoice Total", value: total != null ? currency(total) : pending, tone: "navy" },
    {
      label: "Estimated Fair Payment",
      value: fairPayment != null ? currency(fairPayment) : pending,
      tone: "trust",
    },
    {
      label: "Settlement Goal",
      value: settlementGoal != null ? currency(settlementGoal) : pending,
      tone: "savings",
    },
    {
      label: "Total Supported Recovery",
      value: potential != null ? currency(potential) : pending,
      tone: "savings",
    },

    {
      // SAB-004: report-wide analysis confidence from the findings engine.
      // Same number, same name, same ladder as the report and the workspace.
      label: ANALYSIS_CONFIDENCE_LABEL,
      value: formatConfidencePct(confidence) === NOT_CALCULATED ? pending : `${confidence}%`,
      tone: "trust",
      sub: confidenceBand(confidence) ? confidenceDescriptor(confidence) : undefined,
    },
    {
      label: "Negotiation Strength",
      value: canonical.strength ? canonical.strength : pending,
      tone: "savings",
    },
    /* Evidence Score removed: `ai_findings.evidence_score` is an internal
       pipeline score with no canonical Report Summary field, so it is never
       shown as a customer-facing metric. */

    {
      label: "Applicable Authorities",
      value: legalAuthorities > 0 ? String(legalAuthorities) : pending,
      tone: "trust",
      sub: legalAuthorities > 0 ? "State & federal authority identified" : undefined,
    },
    {
      label: "Current Stage",
      value: report ? "Report Ready" : currentStageLabel,
      tone: "trust",
    },
  ];


  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-trust">
          <Sparkles className="h-4 w-4" /> Invoice Summary
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-trust">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-trust/60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-trust" />
          </span>
          Updating live
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => {
          const toneCls =
            it.tone === "savings"
              ? "text-savings"
              : it.tone === "trust"
                ? "text-trust"
                : "text-navy";
          return (
            <div key={it.label} className="rounded-xl border border-border bg-white p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {it.label}
              </div>
              <div className={`mt-1 text-lg font-semibold ${toneCls}`}>{it.value}</div>
              {it.sub && (
                <div className="mt-0.5 text-xs font-medium text-muted-foreground">{it.sub}</div>
              )}
            </div>
          );
        })}

      </div>
    </div>
  );
}

/* ---------------- Findings preview ---------------- */



/**
 * SINGLE FINANCIAL SOURCE OF TRUTH for this page. Every screen on the review
 * flow (Invoice Summary, Findings, Dashboard, PDF) resolves its dollars from
 * `buildReportFromBundle`, so no two cards can disagree.
 */
function buildReviewReport(
  c: Case | undefined,
  bundle?: FullWorkspaceBundle,
): ReturnType<typeof buildReport> {
  /* NO DEMO DATA: when there is no real review to build from we fall back to
     an empty, honest shape — never to a sample vendor / ZIP / state. */
  const fallback = c
    ? buildReport(c)
    : ({
        invoiceTotal: 0,
        fairRange: [0, 0] as [number, number],
        potentialSavings: [0, 0] as [number, number],
        estimatedSavings: 0,
        savingsPct: [0, 0] as [number, number],
        strength: "Low" as const,
        confidence: 0,
        vendor: "Not available",
        invoiceDate: "Not available",
        invoiceType: "Not available",
        zip: "",
        state: "",
        findings: [],
        laws: [],
        violations: [],
        timeline: [],
        nextSteps: [],
      } as ReturnType<typeof buildReport>);
  return bundle?.review
    ? buildReportFromBundle(
        {
          review: bundle.review,
          invoiceData: bundle.invoiceData,
          findings: bundle.findings,
          findingItems: bundle.findingItems,
          legalResearch: bundle.legalResearch,
          marketComparisons: bundle.marketComparisons,
          marketResearch: bundle.marketResearch,

          report: bundle.report,
        },
        fallback as ReturnType<typeof buildReport>,
      )
    : (fallback as ReturnType<typeof buildReport>);
}

/* ── Jurisdiction helpers (review-scoped, never global) ───────────────── */

const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

/** Normalize "OH" / "Ohio" / "ohio " to a two-letter code, or null. */
function stateCode(raw?: string | null): string | null {
  const v = (raw ?? "").trim();
  if (!v) return null;
  if (/^[A-Za-z]{2}$/.test(v) && STATE_NAMES[v.toUpperCase()]) return v.toUpperCase();
  const entry = Object.entries(STATE_NAMES).find(
    ([, name]) => name.toLowerCase() === v.toLowerCase(),
  );
  return entry ? entry[0] : null;
}

function stateLabel(raw?: string | null): string | null {
  const code = stateCode(raw);
  return code ? STATE_NAMES[code] : null;
}

function isNum(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

function FindingsPreview({
  c,
  bundle,
}: {
  c: Case | undefined;
  bundle?: FullWorkspaceBundle;
}) {
  const navigate = useNavigate();
  const r = buildReviewReport(c, bundle);

  /* ─── Everything below is derived ONLY from this review's persisted
     records. No fixtures, no placeholders, no cross-review data. ─── */

  const invoiceState = stateCode(r.state);
  const stateName = stateLabel(r.state);
  const zipSource = r.zip && /^\d{5}/.test(r.zip) ? r.zip : (bundle?.review?.zip ?? "");
  const zip = /^\d{5}/.test(zipSource) ? zipSource.slice(0, 5) : null;


  // Key findings — persisted finding items / documented charge findings.
  const keyFindings = r.findings.slice(0, 4);

  // CANONICAL SCOPE: the same certified charge inventory the Professional
  // Report renders from. No raw OCR row reaches this page.
  const canonicalCharges = canonicalChargesFor(bundle?.invoiceData?.line_items);

  // Questionable charges — bound to canonical charge identity so a repeated
  // extraction can never print the same charge twice.
  const questionable = selectQuestionableCharges(r.findings, canonicalCharges, 6);

  // Invoice-wide market comparison — canonical charges only.
  const comparisons = (bundle?.marketComparisons ?? []) as MarketComparison[];
  const marketView = buildCanonicalInvoiceMarketComparison(
    bundle?.invoiceData?.line_items,
    comparisons,
  );

  const topVariances = largestVariances(marketView, 4);
  const pendingLabels = Array.from(
    new Set(marketView.uncompared.filter((u) => u.benchmarkPending).map((u) => u.label)),
  ).slice(0, 2);


  // Legal citations — verified authority for THIS review's jurisdiction only.
  // A review with zero persisted legal_research rows means the legal stage
  // produced no usable saved result — that is reported honestly, never as a
  // completed zero-result search.
  const legal = buildLegalSummary(bundle?.legalResearch ?? [], r.state);


  return (
    <div className="mt-8 space-y-6">
      <SavingsSummaryCard
        amount={r.estimatedSavings}
        strength={r.strength}
      />


      <div className="grid gap-6 lg:grid-cols-2">
        <PreviewPanel title="Key Findings" icon={Sparkles} accent="trust">
          {keyFindings.length > 0 ? (
            <ul className="mt-3 space-y-2 text-sm">
              {keyFindings.map((f, i) => (
                <BulletItem
                  key={`${f.item}-${i}`}
                  tone={f.impact === "High" ? "warn" : f.impact === "Medium" ? "savings" : "trust"}
                >
                  <strong>{f.item}</strong>
                  {f.issue ? ` — ${f.issue}` : null}
                </BulletItem>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              No supported key findings are available yet.
            </p>
          )}
        </PreviewPanel>

        <PreviewPanel title="Unique Charges Flagged" icon={AlertTriangle} accent="warn">
          {questionable.length > 0 && (
            <div className="mt-2 text-xs font-medium text-muted-foreground">
              {questionable.length} charge{questionable.length === 1 ? "" : "s"} flagged ·{" "}
              <span className="font-semibold text-navy">
                {currency(questionable.reduce((sum, row) => sum + (row.charge ?? 0), 0))}
              </span>{" "}
              under review
            </div>
          )}
          {questionable.length > 0 ? (
            <div className="mt-3 space-y-2">
              {questionable.map((row, i) => (
                <div
                  key={`${row.item}-${i}`}
                  className="flex items-center justify-between rounded-lg border border-border bg-white p-3 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <AmberDot tone={row.impact === "Low" ? "muted" : "warn"} />
                    <span className="font-medium text-navy">{row.item}</span>
                  </div>
                  <div className="text-muted-foreground">{currency(row.charge)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              No questionable charges were identified.
            </p>
          )}
        </PreviewPanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <PreviewPanel title="Invoice-Wide Market Comparison" icon={MapPin} accent="trust">
          <div className="mt-3 text-xs text-muted-foreground">
            {[zip ? `ZIP ${zip}` : null, stateName].filter(Boolean).join(" · ") ||
              "Service area on file"}
          </div>

          {marketView.comparedCount >= 2 ? (
            <>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Compared charges
                  </div>
                  <div className="mt-1 text-sm font-semibold text-navy">
                    {marketView.comparedCount} of {marketView.totalChargeCount} invoice charges
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Overall position
                  </div>
                  <div className="mt-1 text-sm font-semibold text-warn">
                    {marketView.overallVariancePercent !== null
                      ? `${Math.abs(Math.round(marketView.overallVariancePercent))}% ${
                          marketView.overallVariancePercent >= 0 ? "above" : "below"
                        } comparable local benchmarks`
                      : "Not available"}
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Supported market range
                  </div>
                  <div className="mt-1 text-sm font-semibold text-trust">
                    {currency(marketView.benchmarkLowTotal)} –{" "}
                    {currency(marketView.benchmarkHighTotal)}
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-white p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Invoice charges compared
                  </div>
                  <div className="mt-1 text-sm font-semibold text-navy">
                    {currency(marketView.invoiceComparableTotal)}
                  </div>
                </div>
              </div>

              {(topVariances.length > 0 || pendingLabels.length > 0) && (
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Largest variances
                  </div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {topVariances.map((v) => (
                      <li key={`var-${v.label}`} className="flex items-start gap-2">
                        <AmberDot tone="warn" />
                        <span>
                          <span className="font-medium text-navy">{v.label}</span>
                          {` — ${Math.round(v.variancePercent)}% above local average`}
                        </span>
                      </li>
                    ))}
                    {pendingLabels.map((label) => (
                      <li key={`pending-${label}`} className="flex items-start gap-2">
                        <AmberDot tone="muted" />
                        <span>
                          <span className="font-medium text-navy">{label}</span>
                          {" — benchmark pending"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {marketView.uncompared.length > 0 && (
                <p className="mt-3 text-xs text-muted-foreground">
                  {marketView.uncompared.length} charge
                  {marketView.uncompared.length === 1 ? "" : "s"} had no verified local benchmark
                  available and {marketView.uncompared.length === 1 ? "was" : "were"} excluded from
                  this comparison.
                </p>
              )}
            </>
          ) : marketView.comparedCount > 0 ? (
            <div className="mt-3">
              <div className="text-sm font-semibold text-navy">Limited Market Comparison</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Verified local benchmarks were available for {marketView.comparedCount} invoice
                charge(s). Open the Professional Report for the charge-by-charge analysis.
              </p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              No verified local market comparisons are currently available for this invoice.
            </p>
          )}
        </PreviewPanel>


        <PreviewPanel title="State Laws & Regulations" icon={Scale} accent="trust">
          {legal.total > 0 ? (
            <div className="mt-3 space-y-3 text-sm">
              <p className="text-navy">
                <strong>{legal.total}</strong> applicable{" "}
                {legal.total === 1 ? "authority" : "authorities"} identified
                {legal.stateName ? ` for ${legal.stateName}` : ""}
                {legal.federalCount > 0
                  ? ` (${legal.stateCount} state, ${legal.federalCount} federal)`
                  : ""}
                .
              </p>
              {legal.topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {legal.topics.map((t: string) => (
                    <span
                      key={t}
                      className="rounded-full border border-trust/30 bg-trust-soft px-2.5 py-1 text-[11px] font-medium text-trust"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  Support level: <SupportLevelBadge level={legal.supportLevel} />
                </span>
                {legal.averageConfidence !== null && (
                  <span>
                    Average relevance:{" "}
                    <strong className="text-navy">
                      {Math.round(legal.averageConfidence * 100)}%
                    </strong>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() =>
                  navigate({ to: "/report/$id", params: { id: c ? c.id : "demo" } })
                }
                className="inline-flex items-center gap-1 text-sm font-semibold text-trust hover:underline"
              >
                View Complete Legal Analysis →
              </button>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              {legal.persisted
                ? `No verified ${legal.stateName ?? "state"} legal citations were identified for the charges summarized in this Case Overview. Open the Professional Report for the complete legal-research results.`
                : "Legal research is incomplete for this review — citation verification is pending. No verified legal authority has been saved yet."}
            </p>
          )}

        </PreviewPanel>

      </div>

      <div className="rounded-2xl border border-savings/30 bg-savings-soft p-6 shadow-card">
        <div className="text-xs font-semibold uppercase tracking-wider text-savings">
          Recommended Next Step
        </div>
        <div className="mt-2 font-display text-2xl text-navy">
          Open your professional report and decide how you&apos;d like to proceed.
        </div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          You can negotiate on your own using our findings and letter, or hand it off to our
          experts. Most customers save more when experts negotiate.
        </p>
      </div>

      <div className="grid gap-4">
        <Button
          size="lg"
          className="h-12 px-8 text-base font-semibold"
          onClick={() =>
            c
              ? navigate({ to: "/report/$id", params: { id: c.id } })
              : navigate({ to: "/report/$id", params: { id: "demo" } })
          }
        >
          <FileText className="mr-2 h-5 w-5" />
          Open Professional Report
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="h-12 px-8 text-base font-semibold"
          onClick={() => navigate({ to: "/dashboard", search: {} })}
        >
          Return to Dashboard
        </Button>
      </div>



    </div>
  );
}

/* ---------------- Real review details ---------------- */

function RealReviewDetails({
  files,
  timeline,
  reviewRow,
  legalResearchPersisted,
}: {
  files: ReviewFile[];
  timeline: TimelineEvent[];
  /** Persisted review row — read-only source for Customer Review Preferences. */
  reviewRow: import("@/lib/db/models").Review | null;
  /** False when the legal stage saved no legal_research rows for this review. */
  legalResearchPersisted: boolean;
}) {
  // Hide entries a later event has contradicted (e.g. "Awaiting Payment"
  // once payment has been received).
  const visibleTimeline = suppressSupersededTimelineEvents(timeline);

  /* PRESENTATION ONLY: the customer's own submission answers, read verbatim
     from the persisted review row. Nothing is inferred, summarized or
     recalculated, and blank answers are dropped entirely. */
  const preferences = customerReviewPreferences(reviewRow);

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
      {files.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
            <FileText className="h-4 w-4" /> Uploaded Files
          </div>
          <ul className="mt-4 space-y-2">
            {files.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between rounded-lg border border-border bg-white p-3 text-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 shrink-0 text-trust" />
                  <span className="truncate font-medium text-navy">{f.file_name}</span>
                </div>
                <span className="ml-3 shrink-0 text-xs text-muted-foreground">
                  {f.file_size ? `${Math.max(1, Math.round(f.file_size / 1024))} KB` : ""}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-border pt-5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
              <FileText className="h-4 w-4" /> Customer Review Preferences
            </div>
            <dl className="mt-4 space-y-4">
              {preferences.map((p) => (
                <div key={p.key} className="min-w-0">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {p.label}
                  </dt>
                  <dd
                    className={`mt-1 whitespace-pre-wrap break-words text-sm ${p.value ? "text-navy" : "text-muted-foreground"}`}
                  >
                    {p.value ?? NOT_PROVIDED}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

        </div>
      )}

      {visibleTimeline.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
            <Clock className="h-4 w-4" /> Timeline
          </div>
          <ol className="relative mt-4 space-y-4 border-l border-dashed border-border/70 pl-5">
            {visibleTimeline.map((ev, idx) => {
              const isLegalStage = /legal|statute|regulation/i.test(
                `${ev.event_type} ${ev.title}`,
              );
              const unverifiedLegal = isLegalStage && !legalResearchPersisted;
              const desc = unverifiedLegal
                ? "Citation verification is still pending — no verified legal authority has been saved for this claim yet."
                : (friendlyTimelineDescription(ev) ??
                  fallbackTimelineDescription(ev.title, ev.event_type));
              const isLatest = idx === 0;
              return (
                <li key={ev.id} className="relative">
                  <span
                    className={`absolute -left-[26px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-card ${
                      unverifiedLegal
                        ? "bg-secondary text-muted-foreground"
                        : isLatest
                          ? "bg-trust text-trust-foreground"
                          : "bg-savings text-savings-foreground"
                    }`}
                  >
                    {unverifiedLegal ? (
                      <Clock className="h-3 w-3" />
                    ) : (
                      <CheckCircle2 className="h-3 w-3" />
                    )}
                  </span>
                  <div
                    className={`text-sm font-semibold ${unverifiedLegal ? "text-muted-foreground" : "text-navy"}`}
                  >
                    {unverifiedLegal
                      ? "State laws & regulations — verification pending"
                      : friendlyTimelineTitle(ev)}
                  </div>

                  {desc && (
                    <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {desc}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {new Date(ev.created_at).toLocaleString()}
                  </div>
                </li>
              );
            })}

          </ol>
        </div>
      )}

    </div>
  );
}

function fallbackTimelineDescription(title: string, type?: string): string | undefined {
  const key = `${type ?? ""} ${title}`.toLowerCase();
  if (/submit|received/.test(key)) return "Uploaded securely and added to your review queue.";
  if (/upload|file/.test(key)) return "Document preparation started for OCR extraction.";
  if (/pending|queue/.test(key)) return "Invoice entered the AI processing queue.";
  if (/ocr|reading|extract/.test(key)) return "OCR extraction in progress.";
  if (/normali|organiz/.test(key)) return "Charges organized into standard categories.";
  if (/legal|law|statute/.test(key)) return "Applicable state statutes and regulations located.";
  if (/market|pricing|compare/.test(key)) return "Comparing local facility pricing.";
  if (/finding|opportun/.test(key)) return "Negotiation opportunities being ranked.";
  if (/strategy|negotiat/.test(key)) return "Preparing negotiation strategy and letter draft.";
  if (/report|package/.test(key)) return "Executive findings report assembled.";
  if (/notif|delivery|complete/.test(key)) return "Customer notification and delivery prepared.";
  return undefined;
}

/* ---------------- Small UI ---------------- */

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1.5 text-sm">{value}</div>
    </div>
  );
}

function SupportLevelBadge({ level }: { level: LegalSupportLevel }) {
  const cls =
    level === "Strong"
      ? "border-savings/40 bg-savings-soft text-savings"
      : level === "Moderate"
        ? "border-warn/40 bg-warn/10 text-warn-strong"
        : level === "Limited"
          ? "border-trust/30 bg-trust-soft text-trust"
          : "border-border bg-secondary text-muted-foreground";
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>
      {level}
    </span>
  );
}

const STRENGTH_STARS: Record<string, number> = {
  "Very High": 5,
  High: 4,
  Medium: 3,
  Low: 2,
};

function SavingsSummaryCard({ amount, strength }: { amount: number; strength: string }) {
  // SAB-004: no fabricated star count. When the findings engine reported no
  // strength, the rating line is omitted rather than defaulting to 3/5.
  const stars = STRENGTH_STARS[strength];
  return (
    <div className="rounded-2xl border-2 border-savings/40 bg-savings-soft p-7 shadow-card">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-savings">
        Total Supported Recovery
      </div>
      <div className="mt-2 font-display text-4xl text-navy sm:text-5xl">{currency(amount)}</div>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Based on local market pricing, applicable state law, and documented invoice discrepancies.
      </p>
      {stars != null && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-navy">
            {strength} Negotiation Opportunity · Score {Math.round((stars / 5) * 100)}/100
          </span>
          <span
            className="text-base tracking-[0.18em] text-savings"
            aria-label={`${stars} out of 5`}
          >
            {"★".repeat(stars)}
            <span className="text-muted-foreground/40">{"★".repeat(5 - stars)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
  icon: Icon,
  emphasis,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: "navy" | "trust" | "savings";
  icon: React.ComponentType<{ className?: string }>;
  emphasis?: boolean;
}) {
  const toneCls =
    tone === "savings"
      ? "text-savings bg-savings-soft"
      : tone === "trust"
        ? "text-trust bg-trust-soft"
        : "text-navy bg-secondary";
  return (
    <div
      className={`rounded-2xl border bg-card p-5 shadow-card ${
        emphasis ? "border-savings/40 ring-1 ring-savings/20" : "border-border"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div
          className={`text-xs font-semibold uppercase tracking-wider ${
            emphasis ? "text-savings" : "text-muted-foreground"
          }`}
        >
          {label}
        </div>
      </div>
      <div className={`mt-3 font-semibold text-navy ${emphasis ? "text-3xl" : "text-2xl"}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs font-medium text-muted-foreground">{sub}</div>}
    </div>
  );
}


function PreviewPanel({
  title,
  icon: Icon,
  accent,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: "trust" | "savings" | "warn";
  children: React.ReactNode;
}) {
  const cls =
    accent === "savings" ? "text-savings" : accent === "warn" ? "text-warn" : "text-trust";
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div
        className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${cls}`}
      >
        <Icon className="h-4 w-4" />
        {title}
      </div>
      {children}
    </div>
  );
}

function BulletItem({
  tone,
  children,
}: {
  tone: "savings" | "trust" | "warn";
  children: React.ReactNode;
}) {
  const Icon = tone === "warn" ? AlertTriangle : tone === "savings" ? TrendingDown : CheckCircle2;
  const cls = tone === "warn" ? "text-warn" : tone === "savings" ? "text-savings" : "text-trust";
  return (
    <li className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${cls}`} />
      <span>{children}</span>
    </li>
  );
}

function AmberDot({ tone }: { tone: "warn" | "muted" }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${
        tone === "warn" ? "bg-warn" : "bg-muted-foreground/40"
      }`}
    />
  );
}

function RateBar({
  label,
  value,
  display,
  tone,
  emphasis,
}: {
  label: string;
  value: number;
  display: string;
  tone?: "warn";
  emphasis?: boolean;
}) {
  const barCls = tone === "warn" ? "bg-warn" : "bg-trust";
  const pct = Math.min(100, value);
  return (
    <div className={emphasis ? "rounded-lg bg-warn/5 px-2 py-1.5 ring-1 ring-warn/25" : undefined}>
      <div className="flex items-center justify-between text-xs">
        <span className={emphasis ? "font-semibold text-warn" : "text-muted-foreground"}>
          {label}
        </span>
        <span className={`font-semibold ${emphasis ? "text-base text-warn" : "text-navy"}`}>
          {display}
        </span>
      </div>
      <div
        className={`mt-1 overflow-hidden rounded-full bg-secondary ${emphasis ? "h-3" : "h-2"}`}
      >
        <div className={`h-full rounded-full ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


/* ---------------- AI job status list ---------------- */

const JOB_DESCRIPTION: Record<string, string> = {
  ocr: "Extracting vendor, invoice number, dates, charges, and totals.",
  invoice_normalization:
    "Categorizing storage, administrative, release, environmental, and lien fees.",
  legal_research:
    "Checking state statutes, consumer protections, and notice requirements.",
  market_comparison: "Benchmarking against comparable facilities in your ZIP code.",
  ai_findings:
    "Analyzing pricing gaps, documentation issues, duplicate charges, and excessive fees.",
  negotiation_strategy: "Building recommendations supported by pricing and legal evidence.",
  report_generation: "Assembling your executive report with findings and citations.",
  notification: "Preparing PDF, notifications, and final customer delivery.",
};

function JobStatusList({
  jobs,
  failed,
  activeStep: _activeStep,
}: {
  jobs: ReviewJobRow[];
  failed: boolean;
  activeStep: number;
}) {
  const reviewId = jobs[0]?.review_id;
  const canResume = jobs.some(
    (j) => j.status === "failed" || j.status === "cancelled" || j.status === "retrying",
  );
  const [resuming, setResuming] = useState(false);
  async function resume() {
    if (!reviewId) return;
    setResuming(true);
    try {
      const { resumeAIProcessing } = await import("@/lib/ai.functions");
      const res = await resumeAIProcessing({ data: { review_id: reviewId } });
      console.log("[review] Resume Processing outcomes:", res.outcomes);
    } catch (e) {
      console.error("[review] Resume Processing failed:", e);
    } finally {
      setResuming(false);
    }
  }
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
          <Sparkles className="h-4 w-4" /> AI Processing Pipeline
        </div>
        {canResume && reviewId && (
          <button
            type="button"
            onClick={resume}
            disabled={resuming}
            className="inline-flex items-center gap-1.5 rounded-full border border-trust/30 bg-trust-soft px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-trust transition hover:bg-trust/10 disabled:opacity-60"
          >
            {resuming ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {resuming ? "Resuming…" : "Resume Processing"}
          </button>
        )}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {failed
          ? "A processing stage failed. The details are below — click Resume Processing to retry from the failed stage."
          : "Live status of each processing stage."}
      </div>
      <ul className="mt-4 space-y-2">
        {jobs.map((j) => {
          const isProcessing = j.status === "processing" || j.status === "retrying";
          const isFailed = j.status === "failed";
          const isBlocked = j.status === "cancelled";
          return (
            <li
              key={j.id}
              className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-3 text-sm transition ${
                isProcessing
                  ? "border-trust/40 bg-trust-soft/60 ring-1 ring-trust/15"
                  : isFailed
                    ? "border-warn/40 bg-warn/5"
                    : "border-border/70 bg-white"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-medium text-navy">
                  {isProcessing && (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-trust" />
                  )}
                  {j.status === "completed" && (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-savings" />
                  )}
                  {isFailed && <XCircle className="h-3.5 w-3.5 shrink-0 text-warn" />}
                  <span className="truncate">{JOB_LABEL[j.job_type]}</span>
                </div>
                {JOB_DESCRIPTION[j.job_type] && (
                  <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {JOB_DESCRIPTION[j.job_type]}
                  </div>
                )}
                <JobStamp job={j} />
                {(isFailed || isBlocked) && j.error && (
                  <div className="mt-1.5 rounded-md bg-warn/10 px-2 py-1.5 text-xs leading-relaxed text-warn">
                    {j.error.length > 240 ? `${j.error.slice(0, 240)}…` : j.error}
                  </div>
                )}
              </div>

              <JobStatusBadge status={j.status} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function JobStatusBadge({ status }: { status: ReviewJobRow["status"] }) {
  const map: Record<ReviewJobRow["status"], { label: string; className: string }> = {
    queued: {
      label: "Queued",
      className: "bg-secondary text-muted-foreground",
    },
    processing: {
      label: "Processing",
      className: "bg-trust-soft text-trust",
    },
    completed: {
      label: "Completed",
      className: "bg-savings-soft text-savings",
    },
    failed: {
      label: "Failed",
      className: "bg-warn/10 text-warn",
    },
    retrying: {
      label: "Retrying",
      className: "bg-warn/10 text-warn",
    },
    cancelled: {
      label: "Blocked",
      className: "bg-secondary text-muted-foreground",
    },
  };
  const s = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${s.className}`}
    >
      {s.label}
    </span>
  );
}

/* ---------------- Pipeline pulse (always-visible compact status) ---------------- */

function PipelinePulse({
  activeStep,
  jobs,
}: {
  activeStep: number;
  jobs: ReviewJobRow[];
}) {
  const total = STEPS.length;
  const clamped = Math.min(activeStep, total);
  const pct = Math.round((clamped / total) * 100);
  const currentLabel = STEP_INFO[Math.max(0, clamped - 1)]?.label ?? STEP_INFO[0].label;
  const remaining = Math.max(0, total - clamped);
  const etaMin = Math.max(2, remaining * 4);
  const anyFailed = jobs.some((j) => j.status === "failed");
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-trust/20 bg-gradient-to-br from-trust-soft/60 via-card to-card p-5 shadow-card animate-fade-in sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-trust">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-trust/60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-trust" />
            </span>
            Live · Stage {clamped} of {total}
          </div>
          <div className="mt-1.5 font-display text-lg text-navy sm:text-xl">{currentLabel}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {anyFailed ? "Attention needed" : "Estimated remaining"}
          </div>
          <div
            className={`mt-1 inline-flex items-center gap-1.5 text-sm font-semibold ${
              anyFailed ? "text-warn" : "text-trust"
            }`}
          >
            {anyFailed ? (
              <>
                <AlertTriangle className="h-4 w-4" /> Retry available below
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                {remaining === 0 ? "Wrapping up…" : `~${etaMin} min`}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>Overall progress</span>
          <span className="text-navy">{pct}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-gradient-to-r from-trust to-savings transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Mini stage dots */}
      <ol className="mt-4 grid grid-cols-8 gap-1" aria-hidden>
        {STEP_INFO.map((s, i) => {
          const idx = i + 1;
          const done = idx < clamped;
          const current = idx === clamped;
          return (
            <li
              key={s.label}
              className={`h-1.5 rounded-full transition-colors duration-500 ${
                done
                  ? "bg-savings"
                  : current
                    ? "bg-trust animate-pulse"
                    : "bg-secondary"
              }`}
              title={s.label}
            />
          );
        })}
      </ol>
    </div>
  );
}

/* ---------------- Job timestamp helper ---------------- */

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return "just now";
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function JobStamp({ job }: { job: ReviewJobRow }) {
  if (job.status === "completed" && job.completed_at) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        <CheckCircle2 className="h-3 w-3 text-savings" />
        Completed {relativeTime(job.completed_at)}
      </div>
    );
  }
  if ((job.status === "processing" || job.status === "retrying") && job.started_at) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-trust">
        <Loader2 className="h-3 w-3 animate-spin" />
        Started {relativeTime(job.started_at)}
      </div>
    );
  }
  if (job.status === "queued") {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Clock className="h-3 w-3" />
        Waiting in queue
      </div>
    );
  }
  return null;
}

